import { garantiasDelPdf } from "./garantias";
import {
  columnaResumen,
  id,
  nuevaCotizacion,
  nuevaPieza,
  nuevoProducto,
  nuevoSitio,
  esUnidad,
} from "./model";
import { calcularPieza, costoMateriales, precioAjustadoDe } from "./pricing";
import { esSnapshot } from "./snapshot";
import type {
  Cotizacion,
  Historial,
  Pieza,
  ResultadoCalculo,
  UnidadMedida,
  UnidadesPieza,
  Vendedor,
} from "./types";

export function etiquetaHistorial(payload: unknown): string {
  if (!payload || typeof payload !== "object") return "";
  const etiqueta = (payload as Record<string, unknown>).etiqueta_propuesta;
  return typeof etiqueta === "string" ? etiqueta : "";
}

// El número oficial nunca se arma aquí: el backend lo asigna al guardar
// (numero_cotizacion va null en un borrador) y valida la base pedida para
// propuestas B, C… `entregar` marca la versión que ve el cliente: cambios
// posteriores salen como revisión -R1, -R2…
export function construirPayload(
  c: Cotizacion,
  vendedor: Vendedor,
  guardadoId: string,
  entregar = false,
) {
  return {
    ...(c.etiquetaPropuesta
      ? {
          etiqueta_propuesta: c.etiquetaPropuesta,
          numero_base_solicitado: c.numeroBaseOficial ?? null,
        }
      : {}),
    numero_cotizacion: c.numeroOficial ?? null,
    entregar,
    continuacion_de_id: c.registroId,
    revision_esperada: c.revision,
    guardado_id: guardadoId,
    vendedor,
    cliente: c.cliente,
    margen_general_activo: c.margenActivo,
    margen_default: c.margenActivo ? c.margen : 0,
    descuento_general_pct: c.descuento,
    condiciones: {
      ...c.condiciones,
      oferta: { incluye_iva: c.ivaIncluido, iva_pct: c.ivaPct },
    },
    formato_pdf: c.formatoPdf,
    mostrar_valor_unitario: c.mostrarValorUnitario !== false,
    incluir_garantias: c.incluirGarantias,
    garantias: c.incluirGarantias
      ? c.garantias
          .filter((g) => g.incluida)
          .map(({ categoria, anios, notas }) => ({ categoria, anios, notas }))
      : [],
    sitios: c.sitios.map((s) => {
      const piezas = s.productos.flatMap((pr) => pr.piezas);
      const margenSitio = s.margenOverride ?? (c.margenActivo ? c.margen : 0);
      // n8n todavía aplica imprevistos y mano de obra (tabla Formula de
      // Airtable) a todo lo que no llega como precio final. El catálogo
      // general y las personalizadas ya son precio de venta, así que se
      // mandan como precio final con su margen incluido (n8n no le suma
      // margen a un precio final; el descuento sí lo aplica él). Las piezas
      // por lista de materiales siguen con la fórmula de n8n.
      const ajustes = (p: Pieza) => {
        if (p.precioAjustado != null) return precioAjustadoDe(p);
        const r = calcularPieza(p, margenSitio);
        return r.bypass || r.porMateriales ? null : r.bruto;
      };
      return {
        nombre_sitio: s.nombre,
        direccion_instalacion: s.direccion,
        ciudad: s.ciudad,
        ciudad_personalizada: s.ciudadPersonalizada,
        sede_atiende: s.sede || null,
        margen_override: s.margenOverride,
        addons: s.addons.map(({ id, nombre, costo, cantidades }) => ({
          id,
          nombre,
          costo,
          cantidades,
        })),
        logistica_manual: {
          incluida: s.logistica.incluida,
          visitas: s.logistica.visitas,
          costo_por_visita: s.logistica.costoPorVisita,
          // Sin total manual ni visitas, la pantalla (calcularSitio) cuenta
          // $0, pero con null n8n cobra la tarifa fija de la ciudad (Norte
          // $220): se manda el $0 para que el PDF cobre lo que se ve.
          total_manual:
            s.logistica.totalManual ??
            (s.logistica.incluida && s.logistica.visitas > 0 ? null : 0),
        },
        productos: s.productos.map((pr) => ({
          nombre: pr.nombre,
          columna_resumen: pr.columnaResumen?.trim() || null,
          imagenes: pr.imagenes,
          piezas: pr.piezas.map((p) => ({
            id: p.id,
            familia: p.familia,
            variante: p.variante,
            ancho_cm: p.anchoCm,
            alto_cm: p.altoCm,
            profundidad_cm: p.profundidadCm || null,
            // m² escritos directo por el vendedor; si viene, n8n lo usa en
            // lugar de ancho × alto.
            area_m2: p.areaM2 ?? null,
            unidad_medida: p.unidadMedida ?? "cm",
            // Solo para reabrir la pieza como se escribió; nadie fuera del
            // editor lo lee (medidas siempre en cm arriba).
            unidades_medida: p.unidades ?? null,
            cantidad: p.cantidad,
            precio_contratado: p.precioContratado,
          })),
        })),
        interno: {
          ajustes_precio: piezas
            .filter((p) => ajustes(p) != null)
            .map((p) => ({
              pieza_id: p.id,
              precio_ajustado: ajustes(p),
              // Lo calculó el cotizador; no es un ajuste del vendedor y
              // restaurarCotizacion no lo vuelve precio manual.
              ...(p.precioAjustado == null ? { precio_venta: true } : {}),
            })),
          margenes_pieza: piezas
            .filter((p) => p.margenOverride != null)
            .map((p) => ({
              pieza_id: p.id,
              margen_override: p.margenOverride,
            })),
          descuentos_pieza: piezas
            .filter((p) => p.descuentoPct > 0)
            .map((p) => ({ pieza_id: p.id, descuento_pct: p.descuentoPct })),
          materiales_desglose: piezas
            .filter((p) => p.materiales?.length)
            .map((p) => ({
              pieza_id: p.id,
              materiales: p.materiales,
              costo_materiales: costoMateriales(p),
              personalizado: !!p.personalizado,
            })),
          materiales_referencia: piezas
            .filter((p) => p.materialesReferencia?.length)
            .map((p) => ({
              pieza_id: p.id,
              materiales: p.materialesReferencia,
            })),
          precios_catalogo: piezas.map((p) => ({
            pieza_id: p.id,
            precio_unitario: p.precioUnitario,
            unidad: p.unidad,
            es_personalizada: !!p.esPersonalizada,
          })),
          catalogo_especial_pieza: piezas
            .filter((p) => p.catalogoEspecial)
            .map((p) => ({
              pieza_id: p.id,
              catalogo_especial_id: p.catalogoEspecial!.id,
              codigo_articulo: p.catalogoEspecial!.codigoArticulo,
            })),
        },
      };
    }),
    foto_fachada_url: c.fachadaUrl,
    simulacion_ia_url: c.simulacionUrl,
    interno: { contacto_ghl: c.contacto, sugerencias: construirSugerencias(c) },
    // Snapshot versionado solo para el editor: el contrato comercial anterior
    // permanece intacto y las automatizaciones pueden ignorar este campo.
    editor: { version: 1, cotizacion: c },
  };
}
function construirSugerencias(c: Cotizacion) {
  return c.sitios.flatMap((s) => [
    ...(s.ciudadPersonalizada
      ? [
          {
            tipo: "ciudad",
            nombre: s.ciudadPersonalizada.ciudad,
            costo_fijo: s.ciudadPersonalizada.costoFijo,
          },
        ]
      : []),
    ...s.addons
      .filter((a) => a.personalizado)
      .map((a) => ({ tipo: "addon", nombre: a.nombre, costo: a.costo })),
    ...s.productos.flatMap((pr) =>
      pr.piezas
        .filter((p) => p.personalizado || p.esPersonalizada)
        .map((p) => ({
          tipo: "pieza",
          nombre: p.variante,
          precio_unitario: p.precioUnitario,
          unidad: p.unidad,
          materiales: p.materialesReferencia,
        })),
    ),
  ]);
}
export const registro = (x: unknown): Record<string, unknown> =>
  x && typeof x === "object" && !Array.isArray(x)
    ? (x as Record<string, unknown>)
    : {};
const lista = (x: unknown) => (Array.isArray(x) ? x.map(registro) : []);
const texto = (x: unknown, fallback = "") =>
  typeof x === "string" ? x : fallback;
const numero = (x: unknown, fallback = 0) =>
  typeof x === "number" && Number.isFinite(x) ? x : fallback;
const nullable = (x: unknown) =>
  typeof x === "number" && Number.isFinite(x) ? x : null;
const strings = (x: unknown) =>
  Array.isArray(x) ? x.filter((s): s is string => typeof s === "string") : [];

export function restaurarCotizacion(
  h: Historial,
  duplicar = false,
): Cotizacion {
  const raw = registro(h.payload),
    editor = registro(raw.editor),
    snapshot = registro(editor.cotizacion);
  let c = nuevaCotizacion();
  if (raw.editor !== undefined) {
    if (editor.version !== 1 || !esSnapshot(snapshot))
      throw new Error("Snapshot de cotización incompatible.");
    c = structuredClone(snapshot);
  } else {
    const cliente = registro(raw.cliente ?? h.cliente),
      condiciones = registro(raw.condiciones),
      oferta = registro(condiciones.oferta);
    c.cliente = {
      nombre: texto(cliente.nombre, h.cliente_nombre),
      encargado: texto(cliente.encargado),
      nombre_proyecto: texto(cliente.nombre_proyecto),
      direccion_facturacion: texto(cliente.direccion_facturacion),
      cliente_contrato: texto(cliente.cliente_contrato, "ninguno"),
    };
    c.condiciones = {
      forma_pago: texto(condiciones.forma_pago),
      tiempo_entrega: texto(condiciones.tiempo_entrega),
      validez_oferta: texto(condiciones.validez_oferta),
    };
    c.margenActivo = raw.margen_general_activo !== false;
    c.margen = numero(raw.margen_default, 0.5);
    c.descuento = numero(raw.descuento_general_pct);
    c.ivaIncluido = oferta.incluye_iva === true;
    c.ivaPct = numero(oferta.iva_pct, 15);
    c.formatoPdf = texto(raw.formato_pdf, "consolidado");
    c.incluirGarantias = raw.incluir_garantias !== false;
    c.mostrarValorUnitario = raw.mostrar_valor_unitario !== false;
    if (Array.isArray(raw.garantias))
      c.garantias = lista(raw.garantias).map((g) => ({
        id: id(),
        categoria: texto(g.categoria),
        anios: nullable(g.anios),
        notas: texto(g.notas),
        incluida: true,
      }));
    c.simulacionUrl = texto(raw.simulacion_ia_url);
    c.fachadaUrl = texto(raw.foto_fachada_url);
    const contacto = registro(registro(raw.interno).contacto_ghl);
    if (typeof contacto.id === "string")
      c.contacto = {
        id: contacto.id,
        nombre: texto(contacto.nombre, c.cliente.encargado),
        correo: texto(contacto.correo),
        telefono: texto(contacto.telefono),
      };
    c.correoEnvio = c.contacto?.correo || "";
    c.telefonoEnvio = c.contacto?.telefono || "";
    c.sitios = lista(raw.sitios).map((s) => {
      const interno = registro(s.interno),
        logistica = registro(s.logistica_manual);
      const porPieza = (key: string, piezaId: unknown) =>
        lista(interno[key]).find((x) => x.pieza_id === piezaId) ?? {};
      return {
        ...nuevoSitio(),
        nombre: texto(s.nombre_sitio),
        direccion: texto(s.direccion_instalacion),
        ciudad: texto(s.ciudad),
        sede: texto(s.sede_atiende),
        margenOverride: nullable(s.margen_override),
        ...(s.ciudad_personalizada
          ? {
              ciudadPersonalizada: {
                ciudad: texto(registro(s.ciudad_personalizada).ciudad),
                costoFijo: numero(registro(s.ciudad_personalizada).costoFijo),
                personalizada: true,
              },
            }
          : {}),
        addons: lista(s.addons ?? interno.add_ons).map((a) => ({
          id: texto(a.id, id()),
          nombre: texto(a.nombre),
          costo: numero(a.costo),
          cantidades: a.cantidades
            ? Object.fromEntries(
                Object.entries(registro(a.cantidades)).map(([k, n]) => [
                  k,
                  numero(n),
                ]),
              )
            : null,
          personalizado: texto(a.id).startsWith("personalizado-"),
        })),
        logistica: {
          incluida: logistica.incluida === true,
          visitas: numero(logistica.visitas, 1),
          costoPorVisita: numero(logistica.costo_por_visita),
          totalManual: nullable(logistica.total_manual),
        },
        productos: lista(s.productos).map((pr) => ({
          ...nuevoProducto(),
          nombre: texto(pr.nombre),
          ...(texto(pr.columna_resumen)
            ? { columnaResumen: texto(pr.columna_resumen) }
            : {}),
          imagenes: strings(pr.imagenes),
          piezas: lista(pr.piezas).map((p) => {
            const precio = porPieza("precios_catalogo", p.id),
              mat = porPieza("materiales_desglose", p.id),
              especial = porPieza("catalogo_especial_pieza", p.id);
            const unidad = texto(p.unidad ?? precio.unidad, "m2");
            if (!["m2", "cm2", "cm_lineal", "ml", "unidad"].includes(unidad))
              throw new Error(
                "Unidad histórica no soportada. Requiere revisión de Producción DV.",
              );
            return {
              ...nuevaPieza(),
              id: texto(p.id, id()),
              familia: texto(p.familia),
              variante: texto(p.variante),
              material: texto(p.material),
              iluminacion: texto(p.iluminacion, "N/A"),
              anchoCm: numero(p.ancho_cm),
              altoCm: numero(p.alto_cm),
              profundidadCm: numero(p.profundidad_cm),
              areaM2: nullable(p.area_m2),
              unidadMedida: (["m", "cm", "mm"].includes(String(p.unidad_medida))
                ? p.unidad_medida
                : "cm") as UnidadMedida,
              // Sin unidades por campo (cotizaciones anteriores) se deja
              // undefined a propósito: pisa el default de nuevaPieza() y
              // unidadesDe() vuelve a unidadMedida, como se escribió.
              unidades: unidadesValidas(p.unidades_medida)
                ? p.unidades_medida
                : undefined,
              cantidad: numero(p.cantidad, 1),
              unidad: (["m2", "cm2", "cm_lineal", "ml", "unidad"].includes(
                unidad,
              )
                ? unidad
                : "m2") as Pieza["unidad"],
              precioUnitario: nullable(
                p.precio_unitario ?? precio.precio_unitario,
              ),
              precioContratado: nullable(p.precio_contratado),
              precioAjustado: nullable(
                p.precio_ajustado ??
                  (porPieza("ajustes_precio", p.id).precio_venta === true
                    ? null
                    : porPieza("ajustes_precio", p.id).precio_ajustado),
              ),
              margenOverride: nullable(
                p.margen_override ??
                  porPieza("margenes_pieza", p.id).margen_override,
              ),
              descuentoPct: numero(
                p.descuento_pct ??
                  porPieza("descuentos_pieza", p.id).descuento_pct,
              ),
              esPersonalizada:
                p.es_personalizada === true || precio.es_personalizada === true,
              personalizado:
                p.personalizado === true || mat.personalizado === true,
              materiales: lista(p.materiales ?? mat.materiales).map((m) => ({
                nombre: texto(m.nombre),
                cantidad: numero(m.cantidad),
                precioUnitario: numero(m.precioUnitario),
                porArea: m.porArea === true,
                unidad: texto(m.unidad),
              })),
              materialesReferencia: strings(
                p.materiales_referencia ??
                  porPieza("materiales_referencia", p.id).materiales,
              ),
              catalogoEspecial: especial.catalogo_especial_id
                ? {
                    id: texto(especial.catalogo_especial_id),
                    codigoArticulo: texto(especial.codigo_articulo),
                    clienteMarca: c.cliente.cliente_contrato,
                  }
                : null,
            };
          }),
        })),
      };
    });
  }
  c.numero = duplicar
    ? nuevaCotizacion().numero
    : texto(h.numero_cotizacion, texto(raw.numero_cotizacion, c.numero));
  // Siempre desde la fila, no del snapshot: el snapshot se toma antes de que
  // el backend asigne el número. Registros viejos (COT-…) no tienen número
  // oficial y lo reciben en su próximo guardado.
  c.numeroOficial = duplicar ? null : (h.numero_oficial ?? null);
  c.numeroBaseOficial = duplicar ? null : (h.numero_base ?? null);
  c.registroId = duplicar ? null : h.id;
  if (duplicar) {
    delete c.etiquetaPropuesta;
    delete c.numeroBase;
  } else {
    if (typeof raw.etiqueta_propuesta === "string")
      c.etiquetaPropuesta = raw.etiqueta_propuesta;
    if (typeof raw.numero_cotizacion_base === "string")
      c.numeroBase = raw.numero_cotizacion_base;
  }
  c.revision = duplicar ? null : (h.revision ?? null);
  // Una garantía solo se ofrece con sus años (ver Margen.tsx). Las guardadas
  // antes de esa regla podían ir marcadas sin años ("por confirmar"): al
  // abrirlas se desmarcan para que el vendedor las complete o las deje fuera.
  c.garantias = c.garantias.map((g) =>
    g.incluida && !(g.anios != null && g.anios > 0)
      ? { ...g, incluida: false }
      : g,
  );
  return c;
}

// Sitios que el cálculo autoritativo dejó en $0 (tiendas sin intervención en
// la campaña). En el resumen por tienda y zona el vendedor decide si se
// listan con "-" o se omiten del PDF (ver usePreguntarSitiosEnCero).
export const sitiosEnCero = (r: ResultadoCalculo) =>
  r.sitios
    .filter((s) => Math.abs(s.total_sitio) < 0.005)
    .map((s) => s.nombre_sitio.trim() || "Sitio sin nombre");

// Logística y add-ons del sitio como filas del PDF: la plantilla de n8n solo
// imprime piezas, así que el cliente veía un «Total del sitio» mayor que la
// suma de las filas sin saber por qué. Se arman con lo que cobró n8n.
function servicios(s: ResultadoCalculo["sitios"][number], i: number) {
  const d = s.desglose_interno ?? {};
  const filas = [
    { nombre: "Logística e instalación", costo: d.logistica_ciudad ?? 0 },
    ...(d.addons ?? []).map((a) => ({
      nombre: a.nombre || "Servicio adicional",
      costo: a.costo ?? 0,
    })),
  ].filter((f) => f.costo > 0);
  if (!filas.length) return [];
  return [
    {
      nombre: "Logística y servicios",
      columna_resumen: "Logística y servicios",
      imagenes: [],
      piezas: filas.map((f, j) => ({
        pieza_id: `servicio-${i}-${j}`,
        familia: "Servicios",
        variante: f.nombre,
        cantidad: 1,
        subtotal: f.costo,
        subtotal_bruto: f.costo,
        descuento_pct: 0,
        descuento_monto: 0,
        material: "",
        iluminacion: "N/A",
        medidas: null,
      })),
    },
  ];
}
export function construirPdf(
  c: Cotizacion,
  r: ResultadoCalculo,
  v: Vendedor,
  { incluirSitiosEnCero = true }: { incluirSitiosEnCero?: boolean } = {},
) {
  const productos = new Map<string, number>();
  if (r.sitios.length !== c.sitios.length)
    throw new Error(
      "El cálculo recibido no coincide con los sitios de la cotización.",
    );
  const sitios = r.sitios.map((s, i) => {
    const porId = new Map(s.piezas_detalle.map((p) => [p.pieza_id, p]));
    return {
      ...s,
      productos: c.sitios[i].productos
        .map((pr) => {
          const piezas = pr.piezas.map((p) => {
            const calculada = porId.get(p.id);
            if (!calculada)
              throw new Error(
                "El cálculo recibido no incluye todas las piezas.",
              );
            // Medidas tal como las ingresó el vendedor, solo para imprimirlas en
            // el detalle del PDF (en m y m²). En piezas por unidad son opcionales
            // e informativas: se envían solo si el vendedor escribió alguna.
            const lineal = ["ml", "cm_lineal"].includes(p.unidad);
            const directo = !lineal && p.areaM2 != null;
            const conMedidas =
              p.unidad !== "unidad" ||
              p.anchoCm > 0 ||
              p.altoCm > 0 ||
              p.profundidadCm > 0;
            return {
              ...calculada,
              material: p.material,
              iluminacion: p.iluminacion,
              medidas: conMedidas
                ? {
                    ancho_cm: directo ? null : p.anchoCm || null,
                    alto_cm: lineal || directo ? null : p.altoCm || null,
                    profundidad_cm: p.profundidadCm || null,
                    area_m2: directo ? p.areaM2 || null : null,
                    lineal,
                  }
                : null,
            };
          });
          const nombre = pr.nombre || "(sin nombre)";
          productos.set(
            nombre,
            (productos.get(nombre) ?? 0) +
              piezas.reduce((n, p) => n + p.subtotal, 0),
          );
          return {
            nombre,
            columna_resumen: columnaResumen(pr),
            imagenes: pr.imagenes,
            piezas,
          };
        })
        .concat(servicios(s, i)),
    };
  });
  return {
    ...(c.etiquetaPropuesta
      ? {
          etiqueta_propuesta: c.etiquetaPropuesta,
          numero_cotizacion_base: c.numeroBaseOficial ?? "BORRADOR",
        }
      : {}),
    numero_cotizacion: c.numeroOficial ?? "BORRADOR",
    cliente: c.cliente,
    condiciones: {
      ...c.condiciones,
      oferta: { incluye_iva: c.ivaIncluido, iva_pct: c.ivaPct },
    },
    formato_pdf: c.formatoPdf,
    incluir_sitios_en_cero: incluirSitiosEnCero,
    mostrar_valor_unitario: c.mostrarValorUnitario !== false,
    incluir_garantias: c.incluirGarantias,
    // Generales, del sitio y de cada pieza, con `grupo` (sitio) y `cubre`
    // para que n8n las imprima agrupadas (ver garantiasDelPdf).
    garantias: garantiasDelPdf(c),
    subtotal_sin_iva: r.subtotal_sin_iva,
    iva_incluido: r.iva_incluido,
    iva_pct: r.iva_pct,
    iva_monto: r.iva_monto,
    subtotal_sin_iva_bruto: r.subtotal_sin_iva_bruto,
    descuento_general_pct: r.descuento_general_pct,
    descuento_general_monto: r.descuento_general_monto,
    total_general: r.total_general_interno,
    sitios,
    canal: c.canal,
    contacto_ghl:
      c.canal === "pdf"
        ? null
        : c.contacto
          ? {
              id: c.contacto.id,
              correo: c.correoEnvio,
              telefono: c.telefonoEnvio,
            }
          : null,
    encargado_ghl_id: c.contacto?.id ?? null,
    vendedor_id: v.id,
    // vendedor_nombre: para imprimir en el PDF quién hizo la cotización —
    // vendedor_id solo sirve para resolver al vendedor del lado del backend
    // (GHL), el PDF no puede mostrar un UUID.
    vendedor_nombre: v.nombre,
    // Firma al pie del PDF (datos de "Mi perfil"); sin ella n8n imprime
    // solo vendedor_nombre.
    vendedor_firma: v.firma ?? null,
    // Las fotos que el PDF tiene que poder imprimir: la fachada que subió el
    // vendedor y el montaje que generó el simulador. Iban en construirPayload
    // (el guardado) pero no aquí, así que n8n nunca las recibía al armar el
    // HTML — por eso no salían en el documento.
    foto_fachada_url: c.fachadaUrl,
    simulacion_ia_url: c.simulacionUrl,
    numero_sedes: sitios.length,
    ciudad_del_proyecto:
      [...new Set(sitios.map((s) => s.ciudad).filter(Boolean))].join(", ") ||
      null,
    tipo_producto_principal:
      [...productos].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null,
  };
}
function unidadesValidas(u: unknown): u is UnidadesPieza {
  const r = registro(u);
  return ["ancho", "alto", "profundidad"].every((k) => esUnidad(r[k]));
}
